import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiUrl } from '@/lib/utils';

const API = getApiUrl();

export const BuildingsPage = ({ user, t }) => {
  const navigate = useNavigate();
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    floors: '',
    apartments: '',
    admin_name: '',
    admin_username: '',
    admin_password: '',
    admin_phone: '',
    admin_email: ''
  });

  useEffect(() => {
    if (user?.role !== 'superadmin') {
      navigate('/app');
      return;
    }
    fetchBuildings();
  }, [user, navigate]);

  const fetchBuildings = async () => {
    try {
      const response = await axios.get(`${API}/buildings`, { withCredentials: true });
      setBuildings(response.data);
    } catch (error) {
      toast.error(t('loadDataFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        floors: parseInt(formData.floors),
        apartments: parseInt(formData.apartments)
      };

      if (editingBuilding) {
        await axios.put(`${API}/buildings/${editingBuilding.id}`, payload, { withCredentials: true });
        toast.success(t('buildingUpdated'));
      } else {
        await axios.post(`${API}/buildings`, payload, { withCredentials: true });
        toast.success(t('buildingCreated'));
      }

      setShowModal(false);
      setEditingBuilding(null);
      setFormData({
        name: '',
        address: '',
        floors: '',
        apartments: '',
        admin_name: '',
        admin_username: '',
        admin_password: '',
        admin_phone: '',
        admin_email: ''
      });
      fetchBuildings();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('operationFailed'));
    }
  };

  const handleEdit = (building) => {
    setEditingBuilding(building);
    setFormData({
      name: building.name,
      address: building.address,
      floors: building.floors.toString(),
      apartments: building.apartments.toString(),
      admin_name: building.admin_name || '',
      admin_username: building.admin_username || '',
      admin_password: '',
      admin_phone: building.admin_phone || '',
      admin_email: building.admin_email || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (buildingId) => {
    if (!window.confirm(t('confirmDeleteBuilding'))) return;

    try {
      await axios.delete(`${API}/buildings/${buildingId}`, { withCredentials: true });
      toast.success(t('buildingDeleted'));
      fetchBuildings();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('operationFailed'));
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBuilding(null);
    setFormData({
      name: '',
      address: '',
      floors: '',
      apartments: '',
      admin_name: '',
      admin_username: '',
      admin_password: '',
      admin_phone: '',
      admin_email: ''
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/app')} className="text-gray-600 hover:text-gray-900">
                ← {t('back')}
              </button>
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {t('buildingManagement')}
              </h1>
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-colors"
            >
              {t('createBuilding')}
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('buildingName')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('buildingAddress')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('numberOfFloors')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('numberOfApartments')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('buildingAdmin')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {buildings.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      {t('noBuildings')}
                    </td>
                  </tr>
                ) : (
                  buildings.map((building) => (
                    <tr key={building.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-semibold">{building.name}</td>
                      <td className="px-6 py-4 text-sm">{building.address}</td>
                      <td className="px-6 py-4 text-sm text-center">{building.floors}</td>
                      <td className="px-6 py-4 text-sm text-center">{building.apartments}</td>
                      <td className="px-6 py-4 text-sm">
                        <div>{building.admin_name}</div>
                        <div className="text-xs text-gray-500">{building.admin_username}</div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex gap-3">
                          <button
                            onClick={() => handleEdit(building)}
                            className="text-blue-600 hover:text-blue-700 font-semibold"
                          >
                            {t('edit')}
                          </button>
                          <button
                            onClick={() => handleDelete(building.id)}
                            className="text-red-600 hover:text-red-700 font-semibold"
                          >
                            {t('delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full my-8">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {editingBuilding ? t('editBuilding') : t('createBuilding')}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold mb-2">{t('buildingName')}</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold mb-2">{t('buildingAddress')}</label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg"
                    rows="2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">{t('numberOfFloors')}</label>
                  <input
                    type="number"
                    value={formData.floors}
                    onChange={(e) => setFormData({ ...formData, floors: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">{t('numberOfApartments')}</label>
                  <input
                    type="number"
                    value={formData.apartments}
                    onChange={(e) => setFormData({ ...formData, apartments: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg"
                    required
                  />
                </div>
              </div>

              <div className="border-t pt-6 mb-6">
                <h3 className="text-lg font-bold mb-4">{t('buildingAdmin')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold mb-2">{t('adminFullName')}</label>
                    <input
                      type="text"
                      value={formData.admin_name}
                      onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">{t('adminUsername')}</label>
                    <input
                      type="text"
                      value={formData.admin_username}
                      onChange={(e) => setFormData({ ...formData, admin_username: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg"
                      required={!editingBuilding}
                      disabled={editingBuilding}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">{t('adminPassword')}</label>
                    <input
                      type="password"
                      value={formData.admin_password}
                      onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg"
                      required={!editingBuilding}
                      placeholder={editingBuilding ? t('leaveBlankToKeep') : ''}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">{t('adminPhone')}</label>
                    <input
                      type="tel"
                      value={formData.admin_phone}
                      onChange={(e) => setFormData({ ...formData, admin_phone: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">{t('adminEmail')}</label>
                    <input
                      type="email"
                      value={formData.admin_email}
                      onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                >
                  {editingBuilding ? t('save') : t('create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
